import { useEffect, useState } from "react";
import { CheckCircle, XCircle, FirstAid, BookOpenText, ArrowFatUp, PencilSimple } from "@phosphor-icons/react";
import { api } from "../context/AuthContext";

const PB_EVENTO = {
  candidata: { label: "Candidato salvato", cls: "text-amber-400" },
  applicata: { label: "Rimedio applicato", cls: "text-emerald-400" },
  rifiutata: { label: "Proposta rifiutata", cls: "text-rose-400" },
  promossa: { label: "Promosso a collaudato", cls: "text-cyan-400" },
  modificata: { label: "Voce modificata", cls: "text-sky-400" },
};

const fmtDT = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Rome" });
  } catch { return iso || "—"; }
};

export function PlaybookSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showGov, setShowGov] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState({});
  const startEdit = (v) => {
    setEditId(v.id);
    setForm({ titolo: v.titolo, diagnosi: v.diagnosi, rimedio: v.rimedio, schema: (v.schema || []).join(", "), azione: v.azione || "" });
  };
  const saveEdit = async (id) => {
    setBusy(id);
    try {
      await api.put(`/viki/playbook/${id}`, {
        titolo: form.titolo, diagnosi: form.diagnosi, rimedio: form.rimedio,
        schema_errore: form.schema.split(",").map((s) => s.trim()).filter(Boolean),
        azione: form.azione,
      });
      setEditId("");
      await load();
    } catch { /* validazione backend */ }
    setBusy("");
  };
  const load = () => api.get("/viki/playbook").then(({ data }) => setData(data)).catch(() => setData({ voci: [], governance: [] }));
  useEffect(() => { load(); }, []);
  const promote = async (id) => {
    setBusy(id);
    try { await api.post(`/viki/playbook/${id}/promote`); await load(); } catch { /* solo master */ }
    setBusy("");
  };
  const voci = data?.voci || [];
  const gov = data?.governance || [];
  const titolo = (id) => voci.find((v) => v.id === id)?.titolo || id;
  return (
    <div className="mt-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="playbook-card">
      <div className="flex items-center gap-3 mb-1">
        <BookOpenText size={32} weight="duotone" className="text-cyan-400" />
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Playbook dei rimedi di VIKI</h2>
      </div>
      <p className="text-sm text-slate-400 mb-6">Gli schemi d'errore che VIKI riconosce e i rimedi che propone: quelli <span className="text-emerald-400">collaudati</span> viaggiano sulla corsia veloce a un clic, i <span className="text-amber-400">candidati</span> restano in attesa della promozione di un Master. VIKI non scrive mai codice: propone, il Master decide.</p>
      {data === null && <p className="text-sm text-slate-500">Caricamento…</p>}
      {data && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="playbook-list">
          {voci.map((v) => (
            <div key={v.id} className="border border-white/5 rounded-lg p-4 bg-black/20 flex flex-col" data-testid={`playbook-entry-${v.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${v.stato === "collaudato" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`} data-testid={`playbook-stato-${v.id}`}>
                  {v.stato === "collaudato" ? "Collaudato" : "Candidato"}
                </span>
                {v.azione && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-sky-500/10 text-sky-300 border-sky-500/30">azione: {v.azione}</span>}
              </div>
              <p className="text-sm font-medium text-white mb-1">{v.titolo}</p>
              {editId === v.id ? (
                <div className="space-y-2 mb-2" data-testid={`playbook-edit-form-${v.id}`}>
                  <input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Titolo" data-testid={`playbook-edit-titolo-${v.id}`} />
                  <textarea value={form.diagnosi} onChange={(e) => setForm({ ...form, diagnosi: e.target.value })} rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Diagnosi" data-testid={`playbook-edit-diagnosi-${v.id}`} />
                  <textarea value={form.rimedio} onChange={(e) => setForm({ ...form, rimedio: e.target.value })} rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Rimedio" data-testid={`playbook-edit-rimedio-${v.id}`} />
                  <input value={form.schema} onChange={(e) => setForm({ ...form, schema: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Schema: parole chiave separate da virgola" data-testid={`playbook-edit-schema-${v.id}`} />
                  <input value={form.azione} onChange={(e) => setForm({ ...form, azione: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Azione whitelist (vuoto = solo procedura manuale)" data-testid={`playbook-edit-azione-${v.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(v.id)} disabled={busy === v.id}
                      className="px-3 py-1.5 rounded-md text-[11px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                      data-testid={`playbook-edit-save-${v.id}`}>{busy === v.id ? "Salvo…" : "Salva modifiche"}</button>
                    <button onClick={() => setEditId("")} className="px-3 py-1.5 rounded-md text-[11px] text-slate-400 border border-white/10"
                      data-testid={`playbook-edit-cancel-${v.id}`}>Annulla</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs text-white/60 mb-1"><span className="text-white/40">Diagnosi:</span> {v.diagnosi}</p>
                  <p className="text-xs text-white/60 mb-2"><span className="text-white/40">Rimedio:</span> {v.rimedio}</p>
                  <p className="text-[11px] text-slate-500 mb-2">Schema: {(v.schema || []).map((s) => `"${s}"`).join(", ")}</p>
                </>
              )}
              <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400" data-testid={`playbook-counter-${v.id}`}>
                  Applicata <b className="text-white/80">{v.applicazioni || 0}</b> volte{v.ultima_applicazione ? ` · ultima ${fmtDT(v.ultima_applicazione)}` : ""}
                </span>
                <div className="flex items-center gap-2">
                  {editId !== v.id && (
                    <button onClick={() => startEdit(v)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold text-slate-300 border border-white/10 hover:border-white/25 transition-colors"
                      data-testid={`playbook-edit-btn-${v.id}`}>
                      <PencilSimple size={12} /> Modifica
                    </button>
                  )}
                  {v.stato === "candidato" && (
                    <button onClick={() => promote(v.id)} disabled={busy === v.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/25 transition-colors"
                      data-testid={`playbook-promote-${v.id}`}>
                      <ArrowFatUp size={12} weight="fill" /> {busy === v.id ? "Promuovo…" : "Promuovi a collaudato"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6">
        <button onClick={() => setShowGov(!showGov)} className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="playbook-gov-toggle">
          {showGov ? "Nascondi" : "Mostra"} registro Governance ({gov.length} eventi)
        </button>
        {showGov && (
          <div className="mt-3 space-y-1.5" data-testid="playbook-gov-list">
            {gov.length === 0 && <p className="text-xs text-slate-500">Nessun evento registrato finora.</p>}
            {gov.map((e, i) => {
              const ev = PB_EVENTO[e.evento] || { label: e.evento, cls: "text-slate-400" };
              return (
                <div key={i} className="flex flex-wrap items-center gap-2 text-xs border-b border-white/5 pb-1.5">
                  <span className="text-slate-500 shrink-0">{fmtDT(e.timestamp)}</span>
                  <span className={`font-medium ${ev.cls}`}>{ev.label}</span>
                  <span className="text-white/60 truncate">— {titolo(e.entry_id)}</span>
                  {e.utente && <span className="text-slate-500">({e.utente})</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const CURE_ESITO = {
  guarito: { label: "Guarito", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  parziale: { label: "Parziale", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  "nessun intervento": { label: "Nessun intervento", cls: "bg-white/5 text-slate-400 border-white/10" },
};

export function CureDiary() {
  const [cures, setCures] = useState(null);
  useEffect(() => {
    api.get("/admin/ecosystem/cure/history")
      .then(({ data }) => setCures(data))
      .catch(() => setCures([]));
  }, []);
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Rome" });
    } catch { return iso; }
  };
  return (
    <div className="mt-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="cure-diary-card">
      <div className="flex items-center gap-3 mb-1">
        <FirstAid size={32} weight="duotone" className="text-cyan-400" />
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Diario delle cure di VIKI</h2>
      </div>
      <p className="text-sm text-slate-400 mb-6">Ogni cura dell'ecosistema — automatica (controllo delle 05:00) o avviata da te in chat — con data, riparazioni eseguite ed esito della verifica finale.</p>
      {cures === null && <p className="text-sm text-slate-500">Caricamento…</p>}
      {cures && cures.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="cure-diary-empty">Nessuna cura registrata finora: significa che l'ecosistema non ha mai avuto bisogno di riparazioni.</p>
      )}
      {cures && cures.length > 0 && (
        <div className="space-y-4" data-testid="cure-diary-list">
          {cures.map((c, i) => {
            const esito = CURE_ESITO[c.esito] || CURE_ESITO["nessun intervento"];
            const residui = Object.values(c.residui || {}).flat();
            return (
              <div key={i} className="border border-white/5 rounded-lg p-4 bg-black/20" data-testid={`cure-diary-entry-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-white">{fmtDate(c.eseguita_il)}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${c.origine === "automatica" ? "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30" : "bg-sky-500/10 text-sky-300 border-sky-500/30"}`}>
                    {c.origine === "automatica" ? "Automatica · 05:00" : "Manuale · da chat"}
                  </span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${esito.cls}`} data-testid={`cure-diary-esito-${i}`}>{esito.label}</span>
                </div>
                <ul className="space-y-1">
                  {(c.riparazioni?.length ? c.riparazioni : ["Nessuna riparazione necessaria"]).map((r, ri) => (
                    <li key={ri} className="flex items-start gap-2 text-xs text-white/70">
                      <CheckCircle size={13} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />{r}
                    </li>
                  ))}
                </ul>
                {residui.length > 0 && (
                  <p className="flex items-start gap-2 text-xs text-amber-400/90 mt-2">
                    <XCircle size={13} weight="fill" className="shrink-0 mt-0.5" />
                    Rimasto da sistemare a mano: {residui.join(", ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ST = {
  ok: { label: "ok", dot: "#34D399" },
  warn: { label: "warn", dot: "#FBBF24" },
  pending: { label: "in attesa", dot: "#FBBF24" },
  user_missing: { label: "utenti da riallineare", dot: "#FBBF24" },
  down: { label: "giù", dot: "#F87171" },
  error: { label: "errore", dot: "#F87171" },
  missing: { label: "non installato", dot: "#64748B" },
  unknown: { label: "n/d", dot: "#64748B" },
};
const stOf = (s) => ST[s] || ST.unknown;

const Chip = ({ name, value }) => {
  const st = stOf(value);
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/30 text-white/70">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.dot }} />
      {name}: {st.label}
    </span>
  );
};

export function DoctorCheck() {
  const [visit, setVisit] = useState(null);
  const [running, setRunning] = useState(false);
  const [curing, setCuring] = useState(false);
  const [cureResult, setCureResult] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    setRunning(true); setErr("");
    try {
      const { data } = await api.post("/admin/webapp/doctor", {}, { timeout: 180000 });
      setVisit(data);
    } catch { setErr("Check non riuscito: riprova tra qualche istante."); }
    setRunning(false);
  };
  const cure = async () => {
    setCuring(true); setErr(""); setCureResult(null);
    try {
      const { data } = await api.post("/admin/ecosystem/cure", {}, { timeout: 300000 });
      setCureResult(data);
    } catch { setErr("Cura non riuscita: riprova tra qualche istante."); }
    setCuring(false);
  };
  const deep = visit?.viki_deep || {};
  const portal = visit?.portal || {};
  return (
    <div className="mt-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="doctor-check-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <FirstAid size={32} weight="duotone" className="text-yellow-400" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Check dell'ecosistema</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={run} disabled={running || curing}
            className="px-4 py-2 rounded-md text-xs font-bold bg-yellow-400 text-black border border-yellow-400 hover:bg-yellow-300 transition-colors disabled:opacity-60"
            data-testid="doctor-check-run-btn">
            {running ? "Visita in corso… (~10 secondi)" : "Esegui check adesso"}
          </button>
          <button onClick={cure} disabled={curing || running}
            className="px-4 py-2 rounded-md text-xs font-bold bg-emerald-400 text-black border border-emerald-400 hover:bg-emerald-300 transition-colors disabled:opacity-60"
            data-testid="doctor-cure-run-btn">
            {curing ? "Cura in corso… (~1 minuto)" : "Esegui la cura adesso"}
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-400 mb-4">La stessa visita completa che VIKI esegue ogni notte alle 05:00: sito, API, SSO, sincronizzazione utenti, KPI, dati e collegamenti VIKI di ogni webapp. La cura riallinea gli utenti SSO, ricarica i dati dalle app, svuota la cache e ripete la verifica finale.</p>
      {err && <p className="text-sm text-rose-400" data-testid="doctor-check-error">{err}</p>}
      {cureResult && (
        <div className="mb-4 p-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10" data-testid="doctor-cure-result">
          <p className="text-sm font-bold text-emerald-300 mb-1.5">
            Cura completata{cureResult.esito ? ` — esito: ${cureResult.esito}` : ""}
          </p>
          {(cureResult.interventi || cureResult.riparazioni || []).length > 0 ? (
            <ul className="text-xs text-slate-300 space-y-1">
              {(cureResult.interventi || cureResult.riparazioni).map((r, i) => (
                <li key={i}>• {typeof r === "string" ? r : JSON.stringify(r)}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">Nessuna riparazione necessaria o dettagli nel Diario delle cure qui sotto.</p>
          )}
          <p className="text-[11px] text-slate-500 mt-2">Ora premi "Esegui check adesso" per la verifica: gli stati 'missing' e 'pending' dovrebbero essere rientrati.</p>
        </div>
      )}
      {!visit && !err && !running && <p className="text-sm text-slate-500">Premi il pulsante per la fotografia in tempo reale dell'ecosistema.</p>}
      {visit && (
        <div data-testid="doctor-check-results">
          <p className="text-[11px] text-slate-500 mb-3">Eseguito: {new Date(visit.checked_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "medium", timeZone: "Europe/Rome" })}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {(visit.results || []).map((r) => {
              const st = stOf(r.overall);
              return (
                <div key={r.area} className="border border-white/5 rounded-lg p-4 bg-black/20" data-testid={`doctor-check-app-${r.area}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white capitalize">{r.area}</span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border font-semibold"
                      style={{ color: st.dot, borderColor: st.dot + "55", background: st.dot + "18" }}
                      data-testid={`doctor-check-overall-${r.area}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />
                      {r.overall === "ok" ? "Tutto ok" : st.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Chip name="sito" value={r.site} />
                    <Chip name="API" value={r.api} />
                    <Chip name="SSO" value={r.sso} />
                    <Chip name="sync utenti" value={r.provision} />
                    <Chip name="KPI" value={r.metrics} />
                    <Chip name="marketing" value={r.marketing} />
                    <Chip name="dati VIKI" value={r.viki} />
                    {deep.act_as && deep.act_as[r.area] !== undefined && <Chip name="act-as" value={deep.act_as[r.area]} />}
                  </div>
                  {r.latency_ms != null && <p className="text-[11px] text-slate-500 mt-2">Latenza: {r.latency_ms} ms</p>}
                </div>
              );
            })}
            <div className="border border-white/5 rounded-lg p-4 bg-black/20" data-testid="doctor-check-portal">
              <span className="text-sm font-semibold text-white">Portale AREAPAQ + VIKI</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Chip name="database" value={portal.db} />
                <Chip name="SMTP" value={portal.smtp} />
                <Chip name="Search Console" value={portal.gsc} />
                <Chip name="notifiche push" value={portal.push} />
                <Chip name="widget VIKI" value={deep.widget} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
